import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Text,
} from "@a7y/tailwind-catalyst";
import type { AuditTestRun, SubengineRunResult } from "@a7y/types";
import type { LegacyV1AuditShape } from "@/types/legacyV1Audit";
import {
  CacheOptionsPanel,
  DEFAULT_SKIP_CACHE_OPTIONS,
  type SkipCacheOptions,
  type SkipCacheKey,
} from "@a7y/ui/audit/shared/CacheOptionsPanel";
import { EngineProgressDisplay } from "@a7y/ui/audit/shared/EngineProgressDisplay";
import { EngineResultView } from "@a7y/ui/audit/engine-result/EngineResultView";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  PlayCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StatusBadge,
  PlatformBadge,
  formatDuration,
  formatElapsed,
  toApiCacheOptions,
} from "./shared";

/** Collapsible JSON tree viewer */
function JsonTree({
  data,
  label,
  defaultOpen = false,
}: {
  data: unknown;
  label?: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (data === null || data === undefined) {
    return (
      <span className="text-zinc-400">
        {label && (
          <span className="text-zinc-500 dark:text-zinc-400">{label}: </span>
        )}
        <span className="italic">null</span>
      </span>
    );
  }

  if (typeof data !== "object") {
    return (
      <span>
        {label && (
          <span className="text-zinc-500 dark:text-zinc-400">{label}: </span>
        )}
        <span
          className={
            typeof data === "string"
              ? "text-green-600 dark:text-green-400"
              : typeof data === "number"
                ? "text-blue-600 dark:text-blue-400"
                : typeof data === "boolean"
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-zinc-700 dark:text-zinc-300"
          }
        >
          {typeof data === "string" ? `"${data}"` : String(data)}
        </span>
      </span>
    );
  }

  const isArray = Array.isArray(data);
  const entries = isArray
    ? (data as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(data as Record<string, unknown>);
  const bracket = isArray ? ["[", "]"] : ["{", "}"];

  return (
    <div className="text-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white"
      >
        {isOpen ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {label && (
          <span className="text-zinc-500 dark:text-zinc-400">{label}: </span>
        )}
        <span className="text-zinc-400">
          {bracket[0]}
          {!isOpen && (
            <span className="text-zinc-400">
              {" "}
              {entries.length} {isArray ? "items" : "keys"}{" "}
            </span>
          )}
          {!isOpen && bracket[1]}
        </span>
      </button>
      {isOpen && (
        <div className="ml-4 border-l border-zinc-200 pl-3 dark:border-zinc-700">
          {entries.map(([key, value]) => (
            <div key={key} className="py-0.5">
              <JsonTree data={value} label={key} />
            </div>
          ))}
          <span className="text-zinc-400">{bracket[1]}</span>
        </div>
      )}
    </div>
  );
}

export function AuditTestDetailPage() {
  const { testRunId } = useParams<{ testRunId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRerunDialog, setShowRerunDialog] = useState(false);
  const [cacheOptions, setCacheOptions] = useState<SkipCacheOptions>({
    ...DEFAULT_SKIP_CACHE_OPTIONS,
    all: true,
  });
  const [cacheExpanded, setCacheExpanded] = useState(false);
  const [elapsed, setElapsed] = useState<string>("");

  const {
    data: run,
    isLoading,
    error,
  } = useQuery<AuditTestRun>({
    queryKey: ["audit-test-run", testRunId],
    queryFn: () => api.getAuditTestRun(testRunId!),
    enabled: !!testRunId,
    refetchInterval: (query) => {
      const runData = query.state.data;
      if (runData?.status === "running") {
        return 5000;
      }
      return false;
    },
  });

  // Update elapsed time on an interval when running
  const isRunning = run?.status === "running";
  useEffect(() => {
    if (!isRunning || !run?.createdAt) return;
    // Set initial value immediately. `elapsed` is clock-derived, so it cannot be
    // computed during render (that is react-hooks/purity), and `run.createdAt`
    // only exists once the query resolves — so a useState initializer cannot
    // seed it either. Without this line the timer shows nothing for one second.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setElapsed(formatElapsed(run.createdAt));
    const interval = setInterval(() => {
      setElapsed(formatElapsed(run.createdAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, run?.createdAt]);

  const handleCacheOptionChange = (key: SkipCacheKey, value: boolean) => {
    setCacheOptions((prev) => ({ ...prev, [key]: value }));
  };

  const rerunMutation = useMutation({
    mutationFn: (skipCacheOptions: SkipCacheOptions) =>
      api.triggerAuditTest({
        appName: run!.appName,
        platform: run!.platform,
        triggerSource: "manual",
        skipCacheOptions: toApiCacheOptions(skipCacheOptions),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-test-latest"] });
      queryClient.invalidateQueries({ queryKey: ["audit-test-runs"] });
      setShowRerunDialog(false);
      // Navigate back to list since /run returns a runGroupId, not an individual run ID
      navigate("/admin/audit-testing");
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600"></div>
          <p className="text-gray-600 dark:text-zinc-400">
            Loading test run...
          </p>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="px-4 py-0">
        <PageHeader title="Test Run Not Found" />
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <Text className="text-red-700 dark:text-red-400">
              {error instanceof Error
                ? error.message
                : "Test run not found or could not be loaded."}
            </Text>
          </div>
        </div>
        <Button
          outline
          className="mt-4"
          onClick={() => navigate("/admin/audit-testing")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Audit Testing
        </Button>
      </div>
    );
  }

  const duration =
    run.completedAt && run.createdAt
      ? formatDuration(
          new Date(run.completedAt).getTime() -
            new Date(run.createdAt).getTime()
        )
      : null;

  return (
    <div className="px-4 py-0">
      <PageHeader
        title={`${run.appName} — ${run.platform === "appstore" ? "iOS" : "Android"}`}
        subtitle={`Test Run ${run.id}`}
      >
        <div className="flex items-center gap-3">
          <Button
            outline
            onClick={() => navigate("/admin/audit-testing")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button
            color="blue"
            onClick={() => setShowRerunDialog(true)}
            disabled={rerunMutation.isPending}
          >
            <PlayCircle className="h-4 w-4" />
            Re-run
          </Button>
        </div>
      </PageHeader>

      {/* Running State — use EngineProgressDisplay */}
      {run.status === "running" && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-6 dark:border-blue-900 dark:bg-blue-950">
          <EngineProgressDisplay
            progress={null}
            isLoading={true}
            title="Running audit test..."
            variant="inline"
            auditId={run.auditId}
          />
          <Text className="mt-3 text-sm text-blue-700 dark:text-blue-300">
            Elapsed: {elapsed || "0s"} — Auto-refreshing every 5 seconds
          </Text>
        </div>
      )}

      {/* Summary Bar */}
      <div className="mb-6 rounded-lg bg-white p-4 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <div className="flex flex-wrap items-center gap-4">
          <Text className="text-lg font-semibold text-zinc-900 dark:text-white">
            {run.appName}
          </Text>
          <PlatformBadge platform={run.platform} />
          <StatusBadge status={run.status} />
          {duration && (
            <div className="flex items-center gap-1 text-zinc-500">
              <Clock className="h-4 w-4" />
              <Text className="text-sm">{duration}</Text>
            </div>
          )}
          {run.asoReadinessScore != null && (
            <div className="ml-auto rounded-lg bg-zinc-100 px-3 py-1 dark:bg-zinc-700">
              <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                ASO Readiness Score:{" "}
                <span className="text-lg font-bold text-zinc-900 dark:text-white">
                  {run.asoReadinessScore}
                </span>
              </Text>
            </div>
          )}
        </div>
        {run.createdAt && (
          <Text className="mt-2 text-sm text-zinc-500">
            Started: {new Date(run.createdAt).toLocaleString()}
            {run.completedAt &&
              ` | Completed: ${new Date(run.completedAt).toLocaleString()}`}
            {run.triggerSource && ` | Trigger: ${run.triggerSource}`}
          </Text>
        )}
      </div>

      {/* Error Display */}
      {run.status === "failed" && run.error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div>
              <Text className="font-medium text-red-800 dark:text-red-300">
                Test Failed
              </Text>
              <Text className="mt-1 text-sm text-red-700 dark:text-red-400">
                {run.error}
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Audit Result */}
      {run.status === "passed" && run.auditId && (
        <AuditResultSection auditId={run.auditId} />
      )}

      {/* Raw Run Data */}
      <div className="mt-6 rounded-lg bg-white p-4 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <Text className="mb-3 font-medium text-zinc-900 dark:text-white">
          Raw Test Run Data
        </Text>
        <div className="rounded-md bg-zinc-50 p-4 dark:bg-zinc-900">
          <JsonTree data={run} defaultOpen={true} />
        </div>
      </div>

      {/* Re-run Dialog */}
      <Dialog open={showRerunDialog} onClose={() => setShowRerunDialog(false)}>
        <DialogTitle>
          Re-run: {run.appName} (
          {run.platform === "appstore" ? "iOS" : "Android"})
        </DialogTitle>
        <DialogBody>
          <CacheOptionsPanel
            options={cacheOptions}
            onChange={handleCacheOptionChange}
            expanded={cacheExpanded}
            onToggleExpand={() => setCacheExpanded((prev) => !prev)}
          />
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setShowRerunDialog(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => rerunMutation.mutate(cacheOptions)}
            disabled={rerunMutation.isPending}
          >
            {rerunMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4" />
                Run Test
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

/** Fetches and displays the full audit result using EngineResultView */
function AuditResultSection({ auditId }: { auditId: string }) {
  const { data: auditResult, isLoading } = useQuery<LegacyV1AuditShape>({
    queryKey: ["audit-result", auditId],
    queryFn: () => api.getAuditTestAuditResult(auditId),
    enabled: !!auditId,
  });

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          <Text className="text-sm text-zinc-500">
            Loading audit result...
          </Text>
        </div>
      </div>
    );
  }

  if (!auditResult) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <Text className="text-sm text-zinc-500">
          Audit result not available.
        </Text>
      </div>
    );
  }

  // Use EngineResultView for each sub-engine output if available
  const subengineOutputs = auditResult.subengine_output;
  if (subengineOutputs && subengineOutputs.length > 0) {
    return (
      <div className="space-y-6">
        <Text className="font-medium text-zinc-900 dark:text-white">
          Audit Result — {subengineOutputs.length} Sub-engine{subengineOutputs.length > 1 ? "s" : ""}
        </Text>
        {subengineOutputs.map((output: SubengineRunResult<unknown, unknown, unknown>, idx: number) => (
          <div
            key={output.engine_name ?? idx}
            className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700"
          >
            <EngineResultView
              result={output}
              showInternalTabs={true}
              className=""
            />
          </div>
        ))}
      </div>
    );
  }

  // Fallback to JsonTree if no subengine_output
  return (
    <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
      <Text className="mb-3 font-medium text-zinc-900 dark:text-white">
        Audit Result
      </Text>
      <div className="rounded-md bg-zinc-50 p-4 dark:bg-zinc-900">
        <JsonTree data={auditResult} defaultOpen={true} />
      </div>
    </div>
  );
}
